import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDebounce } from './useDebounce';
import { createBodySampleForGoogle, defaultObjectField } from '@src/utils';
import { useGetPlacesSearchLocationMutation } from '@store/rtk/places';
import {
  getLatLngFromSuggestions,
  hasValidValues,
  parseLatLon,
} from '@utils/helpers';
import { logger } from '@utils/logger';

const validFields = ['address1', 'city', 'state', 'zipCode'];

const parseAddress = (address, placesData, LatLon) => {
  const checkLatLng = hasValidValues(LatLon);
  const coordinates = getLatLngFromSuggestions(placesData);
  let parsedAddress = {
    ...(checkLatLng && { ...LatLon }),
    ...(coordinates && { ...coordinates }),
    street: address,
  };

  return Object.fromEntries(
    Object.entries(parsedAddress).filter(([_, value]) => value)
  );
};

export const usePlacesSearch = (defaultValue: string = '') => {
  const [toggle, setToggle] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>(defaultValue);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [memberPlaces, setMemberPlaces] = useState<any[]>([]);
  const [getPlacesSearchLocation] = useGetPlacesSearchLocationMutation();

  useEffect(() => {
    setSearchQuery(defaultValue);
  }, [defaultValue]);

  const searchPlaces = useCallback(
    async (query: string) => {
      if (!query.trim() || query.length < 3) {
        setSuggestions([]);
        return;
      }

      const { Place } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;

      const request = {
        textQuery: query,
        fields: [
          'displayName',
          'location',
          'nationalPhoneNumber',
          'formattedAddress',
          'googleMapsURI',
          'types',
          'svgIconMaskURI',
          'primaryType',
        ],
        language: 'en-US',
        region: 'us',
      };

      const { places } = await Place.searchByText(request);
      const newPlacesSearchLocation = JSON.parse(JSON.stringify(places));

      if (newPlacesSearchLocation && newPlacesSearchLocation.length > 0) {
        setToggle(true);
        setSuggestions(newPlacesSearchLocation || []);
        const optimizedArray = [
          ...new Set(newPlacesSearchLocation.flatMap((el) => el.types)),
        ];
        const bodyRequestSample = createBodySampleForGoogle(
          validFields,
          optimizedArray,
          query
        );
        const newFields = defaultObjectField({ ...bodyRequestSample }, query);
        const LatLon = parseLatLon(newFields.address1);

        const address = parseAddress(
          newFields.address1,
          newPlacesSearchLocation,
          LatLon
        );
        getPlacesSearchLocation({ data: address })
          .unwrap()
          .then((data) => {
            if (data) {
              setMemberPlaces(data?.locations);
            }
          });
      } else {
        try {
          getPlacesSearchLocation({ data: { street: query } })
            .unwrap()
            .then((data) => {
              if (data) {
                setToggle(true);
                setMemberPlaces(data?.locations);
                if (!places?.length) {
                  setSuggestions([]);
                }
              }
            });
        } catch (error) {
          logger.log({ error });
          setToggle(true);
          setSuggestions([]);
        }
      }
    },
    [getPlacesSearchLocation]
  );

  const debouncedSearch = useDebounce(searchPlaces, 1000);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  return {
    searchQuery,
    setSearchQuery,
    suggestions,
    memberPlaces,
    handleSearchChange,
    toggle,
    setToggle,
  };
};

// TODO this one was just test one, do not remove!!!!
// export const useAutocompletePlacesSearch = (defaultValue: string = '') => {
//   const [toggle, setToggle] = useState<boolean>(false);
//   const [searchQuery, setSearchQuery] = useState<string>(defaultValue);
//   const [suggestions, setSuggestions] = useState<any[]>([]);
//   const [memberPlaces, setMemberPlaces] = useState<any[]>([]);
//   const [getPlacesSearchLocation] = useGetPlacesSearchLocationMutation();
//
//   useEffect(() => {
//     setSearchQuery(defaultValue);
//   }, [defaultValue]);
//
//   const searchPlaces = useCallback(
//     async (query: string) => {
//       if (!query.trim() || query.length < 3) {
//         setSuggestions([]);
//         return;
//       }
//
//       const { AutocompleteSuggestion, AutocompleteSessionToken, Place } =
//         (await google.maps.importLibrary(
//           'places'
//         )) as google.maps.PlacesLibrary;
//
//       const token = new AutocompleteSessionToken();
//
//       const request = {
//         input: query,
//         includedPrimaryTypes: ['establishment', 'geocode'],
//         sessionToken: token,
//         language: 'en-US',
//         region: 'us',
//       };
//
//       const response =
//         await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
//
//       let suggestionsArray: any[] = [];
//       if (response && response.suggestions) {
//         suggestionsArray = response.suggestions;
//       } else if (Array.isArray(response)) {
//         const possibleArray = response[0];
//         if (Array.isArray(possibleArray)) {
//           suggestionsArray = possibleArray;
//         }
//       }
//
//       if (suggestionsArray && suggestionsArray.length > 0) {
//         setToggle(true);
//
//         const mappedSuggestions = suggestionsArray.map((suggestion) => {
//           if (suggestion.placePrediction) {
//             const pred = suggestion.placePrediction;
//             return {
//               id: pred.googlePlaceId,
//               formattedAddress: pred.text?.text || '',
//               displayName: pred.mainText?.text || '',
//               types: pred.types || [],
//               svgIconMaskURI:
//                 'https://maps.gstatic.com/mapfiles/place_api/icons/v1/png_71/generic_business-71.png',
//             };
//           }
//
//           const data =
//             Array.isArray(suggestion) && Array.isArray(suggestion[0])
//               ? suggestion[0]
//               : suggestion;
//
//           if (Array.isArray(data) && data.length >= 5) {
//             return {
//               id: data[1],
//               formattedAddress: data[2]?.[0] || '',
//               displayName: data[3]?.[0]?.[0] || '',
//               types: data[4] || [],
//               svgIconMaskURI:
//                 'https://maps.gstatic.com/mapfiles/place_api/icons/v1/png_71/generic_business-71.png',
//             };
//           }
//
//           return {
//             id: suggestion.place?.id || suggestion.id || '',
//             formattedAddress:
//               suggestion.text?.text || suggestion.formattedAddress || '',
//             displayName:
//               suggestion.structuredFormat?.mainText?.text ||
//               suggestion.displayName ||
//               '',
//             types: suggestion.place?.types || suggestion.types || [],
//             svgIconMaskURI:
//               suggestion.svgIconMaskURI ||
//               'https://maps.gstatic.com/mapfiles/place_api/icons/v1/png_71/generic_business-71.png',
//           };
//         });
//         setSuggestions(mappedSuggestions);
//
//         const firstSuggestion = suggestionsArray[0];
//         let place: google.maps.places.Place | null = null;
//
//         if (firstSuggestion.placePrediction) {
//           place = firstSuggestion.placePrediction.toPlace();
//         } else if (
//           Array.isArray(firstSuggestion) &&
//           Array.isArray(firstSuggestion[0])
//         ) {
//           const id = firstSuggestion[0][1];
//           place = new Place({ id });
//         } else if (firstSuggestion.place) {
//           place = firstSuggestion.place;
//         }
//
//         if (place) {
//           await place.fetchFields({
//             fields: ['location', 'types', 'formattedAddress'],
//           });
//
//           const newPlacesSearchLocation = JSON.parse(JSON.stringify([place]));
//
//           const optimizedArray = [
//             ...new Set(newPlacesSearchLocation.flatMap((el) => el.types)),
//           ];
//           const bodyRequestSample = createBodySampleForGoogle(
//             validFields,
//             optimizedArray,
//             query
//           );
//           const newFields = defaultObjectField({ ...bodyRequestSample }, query);
//           const LatLon = parseLatLon(newFields.address1);
//
//           function parseAddress(address, placesData) {
//             const checkLatLng = hasValidValues(LatLon);
//             const coordinates = getLatLngFromSuggestions(placesData);
//             let parsedAddress = {
//               ...(checkLatLng && { ...LatLon }),
//               ...(coordinates && { ...coordinates }),
//               street: address,
//             };
//
//             return Object.fromEntries(
//               Object.entries(parsedAddress).filter(([_, value]) => value)
//             );
//           }
//
//           const address = parseAddress(
//             newFields.address1,
//             newPlacesSearchLocation
//           );
//
//           getPlacesSearchLocation({ data: address })
//             .unwrap()
//             .then((data) => {
//               if (data) {
//                 setMemberPlaces(data?.locations);
//               }
//             });
//         }
//       } else {
//         setSuggestions([]);
//       }
//     },
//     [getPlacesSearchLocation]
//   );
//
//   const debouncedSearch = useDebounce(searchPlaces, 1000);
//
//   const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
//     const query = e.target.value;
//     setSearchQuery(query);
//     debouncedSearch(query);
//   };
//
//   return {
//     searchQuery,
//     setSearchQuery,
//     suggestions,
//     memberPlaces,
//     handleSearchChange,
//     toggle,
//     setToggle,
//   };
// };
